import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useTCoins, TCOINS_TYPES } from '../../context/TCoinsContext';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase/firebaseConfig';
import { hasRole, ROLES } from '../../utils/roleManager';

const TCoinsPackages = ({ className = "", onClose, onSelectPackage }) => {
  const { currentUser } = useAuth();
  const { addTCoins } = useTCoins();
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [packageConfig, setPackageConfig] = useState({
    starter: { 
      name: 'Starter Pack',
      description: 'แพ็กเกจเริ่มต้น',
      price: 100,
      tcoins: 100,
      bonusType: 'virtual', 
      bonusAmount: 0,
      color: 'silver',
      popular: false,
      enabled: true
    },
    popular: { 
      name: 'Popular Pack',
      description: 'แพ็กเกจยอดนิยม',
      price: 500,
      tcoins: 500,
      bonusType: 'virtual', 
      bonusAmount: 25,
      color: 'gold',
      popular: true,
      enabled: true
    },
    premium: { 
      name: 'Premium Pack',
      description: 'แพ็กเกจพรีเมียม',
      price: 1000,
      tcoins: 1000,
      bonusType: 'virtual', 
      bonusAmount: 100,
      color: 'emerald',
      popular: false,
      enabled: true
    },
    vip: { 
      name: 'VIP Pack',
      description: 'แพ็กเกจ VIP',
      price: 5000,
      tcoins: 5000,
      bonusType: 'virtual', 
      bonusAmount: 750,
      color: 'onyx',
      popular: false,
      enabled: true
    },
    legendary: { 
      name: 'Legendary Pack',
      description: 'แพ็กเกจตำนาน',
      price: 10000,
      tcoins: 10000,
      bonusType: 'virtual', 
      bonusAmount: 2000,
      color: 'purple',
      popular: false,
      enabled: true
    }
  });

  useEffect(() => {
    loadPackageConfig();
  }, []);

  const loadPackageConfig = async () => {
    try {
      const configRef = doc(db, 'config', 'packages');
      const configSnap = await getDoc(configRef);
      
      if (configSnap.exists()) {
        setPackageConfig(configSnap.data());
      }
    } catch (error) {
      console.error('Error loading package config:', error);
    }
  };

  const getPackageData = () => {
    // Convert config to package array format and filter only enabled packages
    return Object.entries(packageConfig)
      .filter(([packageId, config]) => config.enabled) // Only show enabled packages
      .map(([packageId, config]) => ({
        id: packageId,
        name: config.name,
        description: config.description,
        price: config.price,
        tcoins: config.tcoins,
        bonus: config.bonusAmount,
        bonusType: config.bonusType,
        color: config.color,
        popular: config.popular,
        enabled: config.enabled
      }))
      .sort((a, b) => a.price - b.price); // Sort by price from low to high
  };

  const packages = getPackageData();

  const handleSelectPackage = (pkg) => {
    if (onSelectPackage) {
      const packageData = {
        id: pkg.id,
        name: pkg.name,
        description: pkg.description,
        price: pkg.price,
        amount: pkg.tcoins + pkg.bonus,
        tcoins: pkg.tcoins,
        bonus: pkg.bonus,
        bonusType: pkg.bonusType,
        color: pkg.color
      };
      onSelectPackage(packageData);
    }
  };

  const handlePurchase = async (pkg) => {
    if (!currentUser || isProcessing) return;

    try {
      setIsProcessing(true);
      
      // Simulate payment processing
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Add base T Coins (always Real)
      const baseSuccess = await addTCoins(
        currentUser.uid, 
        TCOINS_TYPES.REAL, 
        pkg.tcoins, 
        `ซื้อแพ็กเกจ ${pkg.name} - T Coins หลัก`
      );

      // Add bonus T Coins (based on config)
      let bonusSuccess = true;
      if (pkg.bonus > 0) {
        const bonusType = pkg.bonusType === 'virtual' ? TCOINS_TYPES.VIRTUAL : TCOINS_TYPES.REAL;
        bonusSuccess = await addTCoins(
          currentUser.uid,
          bonusType,
          pkg.bonus,
          `ซื้อแพ็กเกจ ${pkg.name} - โบนัส ${pkg.bonusType === 'virtual' ? 'Bonus Coins' : 'T Coins'}`
        );
      }

      const success = baseSuccess && bonusSuccess;

      if (success) {
        alert(`ซื้อแพ็กเกจ ${pkg.name} สำเร็จ! ได้รับ ${pkg.tcoins + pkg.bonus} T Coins`);
        setSelectedPackage(null);
        if (onClose) onClose();
      } else {
        alert('เกิดข้อผิดพลาดในการซื้อแพ็กเกจ');
      }
    } catch (error) {
      console.error('Error purchasing package:', error);
      alert('เกิดข้อผิดพลาดในการซื้อแพ็กเกจ');
    } finally {
      setIsProcessing(false);
    }
  };

  const getPackageColor = (color) => {
    const colors = {
      silver: 'bg-gray-100 border-gray-300 text-gray-800',
      gold: 'bg-yellow-100 border-yellow-300 text-yellow-800',
      emerald: 'bg-green-100 border-green-300 text-green-800',
      onyx: 'bg-gray-800 border-gray-600 text-white',
      purple: 'bg-purple-100 border-purple-300 text-purple-800'
    };
    return colors[color] || colors.silver;
  };

  const getPackageIcon = (color) => {
    const icons = {
      silver: '🪙',
      gold: '🪙',
      emerald: '🪙',
      onyx: '🪙',
      purple: '🪙'
    };
    return icons[color] || '🪙';
  };

  return (
    <div className={`bg-white rounded-lg shadow-lg p-4 md:p-6 ${className}`}>
      <div className="flex justify-between items-center mb-4 md:mb-6 pr-8 md:pr-0">
        <h2 className="text-lg md:text-2xl font-bold text-gray-800">💰 T Coins Packages</h2>
        {/* Close button is now handled by parent modal */}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
        {packages.length === 0 ? (
          <div className="col-span-full text-center py-12">
            <div className="text-6xl mb-4">😔</div>
            <h3 className="text-xl font-semibold text-gray-600 mb-2">ไม่มีแพ็คเกจที่เปิดใช้งาน</h3>
            <p className="text-gray-500">กรุณาติดต่อแอดมินเพื่อเปิดใช้งานแพ็คเกจ</p>
          </div>
        ) : (
          packages.map((pkg) => (
          <div
            key={pkg.id}
            className={`relative border-2 rounded-lg p-4 cursor-pointer transition-all ${
              selectedPackage?.id === pkg.id
                ? 'border-blue-500 bg-blue-50'
                : 'border-gray-200 hover:border-gray-300'
            } ${getPackageColor(pkg.color)}`}
            onClick={() => setSelectedPackage(pkg)}
          >
            {pkg.popular && (
              <div className="absolute -top-2 -right-2 bg-red-500 text-white text-xs px-2 py-1 rounded-full">
                ยอดนิยม
              </div>
            )}
            
            <div className="text-center">
              <div className="text-3xl mb-2">{getPackageIcon(pkg.color)}</div>
              <h3 className={`text-lg font-bold mb-1 ${pkg.color === 'onyx' ? 'text-white' : 'text-gray-900'}`}>{pkg.name}</h3>
              <p className={`text-sm mb-3 ${pkg.color === 'onyx' ? 'text-gray-300' : 'text-gray-700'}`}>{pkg.description}</p>
              
              <div className="space-y-2">
                <div className={`text-2xl font-bold ${pkg.color === 'onyx' ? 'text-white' : 'text-gray-900'}`}>
                  {pkg.tcoins.toLocaleString()} T Coins
                </div>
                
                {pkg.bonus > 0 && (
                  <div className={`text-sm text-white px-2 py-1 rounded-full ${
                    pkg.bonusType === 'virtual' ? 'bg-red-500' : 'bg-red-500'
                  }`}>
                    +{pkg.bonus} Bonus Coins
                  </div>
                )}
                
                <div className={`text-lg font-bold ${pkg.color === 'onyx' ? 'text-white' : 'text-gray-900'}`}>
                  ฿{pkg.price.toLocaleString()}
                </div>
              </div>
            </div>
          </div>
          ))
        )}
      </div>

      {selectedPackage && (
        <div className="mt-6 p-4 bg-blue-50 rounded-lg">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-bold text-gray-900">แพ็กเกจที่เลือก: {selectedPackage.name}</h3>
            <div className="text-2xl">{getPackageIcon(selectedPackage.color)}</div>
          </div>
          
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <div className="text-sm text-gray-800 font-medium">T Coins พื้นฐาน:</div>
              <div className="font-bold text-gray-900">{selectedPackage.tcoins.toLocaleString()} T Coins</div>
            </div>
            <div>
              <div className="text-sm text-gray-800 font-medium">โบนัส:</div>
              <div className="font-bold text-green-700">+{selectedPackage.bonus} Bonus Coins</div>
            </div>
            <div>
              <div className="text-sm text-gray-800 font-medium">รวมทั้งหมด:</div>
              <div className="font-bold text-blue-700 text-lg">
                {(selectedPackage.tcoins + selectedPackage.bonus).toLocaleString()} T Coins
              </div>
            </div>
            <div>
              <div className="text-sm text-gray-800 font-medium">ราคา:</div>
              <div className="font-bold text-gray-900 text-lg">฿{selectedPackage.price.toLocaleString()}</div>
            </div>
          </div>
          
          <button
            onClick={() => onSelectPackage ? handleSelectPackage(selectedPackage) : handlePurchase(selectedPackage)}
            disabled={isProcessing}
            className="w-full bg-blue-500 text-white font-semibold py-3 px-4 rounded-lg hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isProcessing ? 'กำลังประมวลผล...' : (onSelectPackage ? `เลือกแพ็กเกจ ${selectedPackage.name}` : `ซื้อแพ็กเกจ ${selectedPackage.name}`)}
          </button>
        </div>
      )}

      <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
        <div className="text-center">
          <p className="text-sm text-yellow-800 font-medium mb-2">
            💡 ข้อมูลสำคัญเกี่ยวกับ T Coins และ Bonus Coins
          </p>
          <div className="text-xs text-yellow-700 space-y-1">
            {hasRole(currentUser, ROLES.USER) || hasRole(currentUser, ROLES.FARM) ? (
              // Marketplace User - ข้อความสำหรับตลาดซื้อขาย
              <>
                <p>• T Coins เป็นค่าธรรมเนียมในระบบเท่านั้น</p>
                <p>• ใช้สำหรับซื้อสินค้าในตลาดค้าส่ง</p>
                <p>• ใช้สำหรับจ่ายค่าธรรมเนียมการขายสินค้า</p>
                <p>• ใช้สำหรับเข้าร่วมประมูลและซื้อสินค้าประมูล</p>
                <p className="text-red-600 font-semibold">• Bonus Coins สงวนสิทธิ์ใช้งานในระบบเท่านั้น แลกเป็นสดเงินไม่ได้</p>
              </>
            ) : (
              // Non-Marketplace User - ข้อความเดิม (ซ่อนข้อความเกี่ยวกับน้องและโม)
              <>
                <p>• T Coins เป็นค่าธรรมเนียมในระบบเท่านั้น</p>
                <p>• ใช้สำหรับซื้อของขวัญและจองคิวในระบบ</p>
                <p className="text-red-600 font-semibold">• Bonus Coins สงวนสิทธิ์ใช้งานในระบบเท่านั้น แลกเป็นสดเงินไม่ได้</p>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TCoinsPackages;

